import { z } from 'zod';
import { dateTimeSchema } from './fields';

// ───────────────────────────── Connection (FR-JIRA-001…006, FR-WS-011) ─────────────────────────────

export const JIRA_CONNECTION_STATUSES = [
  'not_configured',
  'configured',
  'connected',
  'disconnected',
] as const;
export type JiraConnectionStatus = (typeof JIRA_CONNECTION_STATUSES)[number];

/** Result of "Pārbaudīt savienojumu" (FR-JIRA-004); the UI words each one. */
export const JIRA_TEST_OUTCOMES = ['ok', 'auth_failed', 'http_error', 'timeout', 'unreachable'] as const;
export type JiraTestOutcome = (typeof JIRA_TEST_OUTCOMES)[number];
export const JIRA_TEST_WARNINGS = ['no_browse_permission', 'permission_check_failed'] as const;
export type JiraTestWarning = (typeof JIRA_TEST_WARNINGS)[number];

/** `https://site.atlassian.net//` → `https://site.atlassian.net`; only https (FR-JIRA-002). */
export const jiraBaseUrlSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/\/+$/, ''))
  .superRefine((v, ctx) => {
    let url: URL;
    try {
      url = new URL(v);
    } catch {
      ctx.addIssue({ code: 'custom', message: 'Nederīgs Jira URL.' });
      return;
    }
    if (url.protocol !== 'https:') ctx.addIssue({ code: 'custom', message: 'Jira URL jābūt ar https://.' });
  });

export const JIRA_TOKEN_REQUIRED = 'Pirmo reizi konfigurējot, jāievada Jira API token.';

export const jiraConnectionInputSchema = z.object({
  baseUrl: jiraBaseUrlSchema,
  email: z
    .string()
    .trim()
    .max(320)
    .refine((v) => v.includes('@'), { message: 'Nepieciešams derīgs e-pasts.' }),
  /** Empty — keep the stored token (FR-JIRA-002). */
  token: z
    .string()
    .trim()
    .max(1000)
    .transform((v) => v || null)
    .nullable()
    .optional(),
});
export type JiraConnectionInput = z.input<typeof jiraConnectionInputSchema>;

/** What the settings page sees — never the token (FR-JIRA-006). */
export const jiraConnectionSchema = z.object({
  status: z.enum(JIRA_CONNECTION_STATUSES),
  baseUrl: z.string().nullable(),
  email: z.string().nullable(),
  hasToken: z.boolean(),
  /** Jira account the token belongs to, from the last successful test. */
  accountName: z.string().nullable(),
  lastTestedAt: dateTimeSchema.nullable(),
  lastTest: z
    .object({ outcome: z.enum(JIRA_TEST_OUTCOMES), httpStatus: z.number().int().nullable() })
    .nullable(),
});
export type JiraConnection = z.infer<typeof jiraConnectionSchema>;

export const jiraTestResultSchema = z.object({
  outcome: z.enum(JIRA_TEST_OUTCOMES),
  httpStatus: z.number().int().nullable(),
  warnings: z.array(z.enum(JIRA_TEST_WARNINGS)),
  connection: jiraConnectionSchema,
});
export type JiraTestResult = z.infer<typeof jiraTestResultSchema>;

/** The Jira site every member may link to (FR-PRJ-015) — not a setting. */
export const jiraSiteSchema = z.object({ baseUrl: z.string().nullable() });

/** `<base>/browse/<KEY>` (FR-PRJ-015, FR-JIRA-015, FR-BIZ-015). */
export const jiraBrowseUrl = (baseUrl: string, key: string): string =>
  `${baseUrl}/browse/${encodeURIComponent(key)}`;

// ───────────────────────────── Mapping to PMAI projects (FR-JIRA-010, FR-BIZ-005) ─────────────────────────────

export interface JiraKeyedProject<I> {
  id: I;
  jiraProjectKeys: readonly string[];
  jiraEpicKeys: readonly string[];
}

/**
 * The one Jira → PMAI project resolver for worklogs and tasks: the epic keys first (in the order
 * given — a worklog's epic, then its parent), then the Jira project key; case-insensitive; when
 * several projects claim a key, the first one given wins.
 */
export function jiraProjectResolver<I>(projects: readonly JiraKeyedProject<I>[]) {
  const byEpic = new Map<string, I>();
  const byKey = new Map<string, I>();
  const index = (map: Map<string, I>, keys: readonly string[], id: I) => {
    for (const key of keys) if (!map.has(key.toUpperCase())) map.set(key.toUpperCase(), id);
  };
  for (const p of projects) {
    index(byEpic, p.jiraEpicKeys, p.id);
    index(byKey, p.jiraProjectKeys, p.id);
  }
  return (epics: readonly (string | null | undefined)[], projectKey: string | null | undefined): I | null => {
    for (const epic of epics)
      if (epic && byEpic.has(epic.toUpperCase())) return byEpic.get(epic.toUpperCase())!;
    return projectKey ? (byKey.get(projectKey.toUpperCase()) ?? null) : null;
  };
}

// ───────────────────────────── Background jobs ─────────────────────────────

/** A Jira job of the workspace (worklog load, task sync): one at a time, in the background. */
export const jobStateSchema = <T extends z.ZodType>(result: T) =>
  z.object({
    running: z.boolean(),
    /** Start of the run in progress or of the last one. */
    startedAt: dateTimeSchema.nullable(),
    lastRunAt: dateTimeSchema.nullable(),
    lastFullSyncAt: dateTimeSchema.nullable(),
    lastResult: result.nullable(),
    lastError: z.string().nullable(),
  });
